import { apiClient } from './api'
import { track } from '../utils/analytics'

export interface BillingStatus {
  plan_tier: 'trial' | 'free' | 'trial_expired' | 'starter' | 'growth' | 'business' | 'enterprise' | 'standalone'
  trial_days_remaining: number | null
  trial_expired: boolean
  trial_ends_at: string | null
  /** Tier whose features are unlocked now; an active trial runs at Growth level. */
  effective_tier?: PlanTier | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  is_paid: boolean
  stripe_enabled: boolean
}

export interface CheckoutResult {
  url: string | null
  stripe_enabled: boolean
}

/** Sprint 8 — superuser-only health probe. Designed to surface a "Stripe
 *  isn't live yet" banner so the org owner can spot the misconfiguration
 *  before a customer hits the Upgrade button. See backend
 *  /api/v1/billing/health for the contract; secret values NEVER appear
 *  in this payload. */
export interface StripeHealth {
  deployment_mode: string
  stripe_enabled: boolean
  secret_key_present: boolean
  secret_key_mode: 'live' | 'test' | 'unknown' | null
  secret_key_length: number
  webhook_secret_present: boolean
  webhook_secret_length: number
  price_starter_configured: boolean
  price_growth_configured: boolean
  price_business_configured: boolean
  success_url: string
  cancel_url: string
  live_mode_ready: boolean
  warnings: string[]
}

export type PlanTier = BillingStatus['plan_tier']

// ── Tier metadata ─────────────────────────────────────────────────────────────

export const PLAN_META: Record<string, { label: string; price: string; color: string }> = {
  // Per-seat pricing (2026-09-27). Keep in sync with plan_guard._PLAN_PRICES
  // and website/src/components/website/PricingPage.tsx.
  trial:         { label: 'Free Trial',  price: 'Free',          color: '#7c3aed' },
  free:          { label: 'Free',        price: '$0 · up to 5 users', color: '#64748b' },
  trial_expired: { label: 'Trial Ended', price: '—',             color: '#ef4444' },
  starter:       { label: 'Starter',     price: '$6/user/mo',    color: '#3b82f6' },
  growth:        { label: 'Growth',      price: '$10/user/mo',   color: '#7c3aed' },
  business:      { label: 'Business',    price: '$20/user/mo',   color: '#1d4ed8' },
  enterprise:    { label: 'Enterprise',  price: 'Custom',  color: '#15803d' },
  standalone:    { label: 'Standalone',  price: 'Licensed', color: '#15803d' },
}

const TIER_ORDER: Record<string, number> = {
  trial_expired: 0,
  trial: 1,
  free: 1,
  starter: 2,
  growth: 3,
  business: 4,
  enterprise: 5,
  standalone: 5,
}

export function tierAtLeast(current: PlanTier, minimum: PlanTier): boolean {
  return (TIER_ORDER[current] ?? 0) >= (TIER_ORDER[minimum] ?? 99)
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const billingService = {
  async getStatus(): Promise<BillingStatus> {
    const { data } = await apiClient.get<BillingStatus>('/api/v1/billing/status')
    return data
  },

  async createCheckout(tier: string): Promise<CheckoutResult> {
    const { data } = await apiClient.post<CheckoutResult>('/api/v1/billing/checkout', { tier })
    track('checkout_started', { tier })
    return data
  },

  async openPortal(): Promise<CheckoutResult> {
    const { data } = await apiClient.post<CheckoutResult>('/api/v1/billing/portal')
    return data
  },

  /** Superuser-only. Returns ``null`` on 401/403 so non-superuser callers
   *  can use this without an error path. */
  async getStripeHealth(): Promise<StripeHealth | null> {
    try {
      const { data } = await apiClient.get<StripeHealth>('/api/v1/billing/health')
      return data
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401 || status === 403) return null
      throw err
    }
  },

  async signup(payload: {
    full_name: string
    email: string
    password: string
    org_name: string
    industry?: string
  }) {
    const { data } = await apiClient.post('/api/v1/auth/signup', payload)
    track('signup_completed', { industry: payload.industry ?? null })
    return data
  },
}
